// 도감 2.0 게임 연결(C6 1단계, 계약 docs/prd/c6-codex-1-contract.md 3절; 선례 game/mastery.ts).
// 판 동안 MobKind별 처치를 고정 배열(할당 없음)로 세고, 스테이지·일일·레이드 영수증이 기록되는 지점에서 한 번 settle한다(영수증 id 멱등).
// 점수 = 몬스터·보스(저장) + 세트(보유 부위) + 펫(0, L2) + 유니크(모험 장부 found) + 아바타(보유).
// 점수 계산은 로드·마을 진입(refreshStats)·정산 때만 한다(프레임 루프 아님). 보너스는 솔로 PvE 판에서만 deriveStats·드롭률에 들어간다(game.ts).
import { CODEX_CATS, CODEX_CELLS, CODEX_BONUS_STEPS, CODEX_COMPLETE_STEP, CODEX_SCORE_MAX, CODEX_CAT_SIZE, CODEX_STEP_SCORES, ZERO_ACCOUNT_BONUS, cellScore, cellStage, codexBonus, makeAccountBonus,
  monsterCellId, bossCellId, nextCodexStep, sumAccountBonus, type AccountBonusIn, type CodexCat, type Mutable } from '../shared/codex';
import type { CodexCatView, CodexCellView, CodexView } from '../shared/codexView';
import { equipDef } from '../shared/gear';
import { SETS } from '../shared/content/items';
import { Ev } from '../shared/events';
import { MOB_KIND_COUNT } from '../shared/types';
import type { GameSim } from './game';
import { CodexStore, type CodexGain } from './codexStore';
import type { SaveStorage } from './save';

type CellMut = Mutable<CodexCellView>;
type CatMut = Mutable<CodexCatView>;

/** 세트 id → 4부위 슬롯 목록 순서(값 = 보유한 서로 다른 부위 수) */
const SET_INDEX = new Map<string, number>(SETS.map((s, i) => [s.id, i]));
const CELL_COUNT = CODEX_CELLS.length;
const CAT_INDEX = Object.fromEntries(CODEX_CATS.map((c, i) => [c, i])) as Record<CodexCat, number>;

export class CodexController {
  readonly store: CodexStore;
  /** 이번 판 MobKind별 처치 수(고정 배열, 판마다 0으로) */
  private readonly runKills = new Uint32Array(MOB_KIND_COUNT);
  /** 이번 판에 쓰러뜨린 보스 키(없으면 null) */
  private runBoss: string | null = null;
  private readonly cellList: CellMut[] = CODEX_CELLS.map((c, index) => ({ index, id: c.id, cat: c.cat, name: c.name, region: c.region, value: 0, stage: 0, score: 0 }));
  private readonly catList: CatMut[] = CODEX_CATS.map((cat) => ({ cat, found: 0, total: CODEX_CAT_SIZE[cat], score: 0 }));
  /** 도감 보너스(출처 상한 적용 뒤, 도감 열만) */
  private readonly bonusOut: Mutable<AccountBonusIn> = makeAccountBonus();
  private readonly rawBonus: Mutable<AccountBonusIn> = makeAccountBonus();
  private scoreNow = 0;
  private bonusStepsReached = 0;
  private rev = 0;
  private lastStoreRev = -1;
  private readonly setSlots = new Map<string, Set<string>>();
  private readonly prevStage = new Uint8Array(CELL_COUNT);
  readonly view: CodexView;

  constructor(private readonly g: GameSim, storage: SaveStorage) {
    this.store = new CodexStore(storage);
    const self = this;
    this.view = {
      get revision() { return self.rev; },
      get score() { return self.scoreNow; },
      max: CODEX_SCORE_MAX,
      cats: this.catList,
      get next() { return nextCodexStep(self.scoreNow); },
      bonus: this.bonusOut,
      get active() { return self.g.codexBonusActive(); },
      cells: () => this.cellList,
    };
    this.recompute(false);
  }

  /** 도감 점수 */
  get score(): number { return this.scoreNow; }
  /** 도감이 주는 보너스(출처 상한 적용 뒤). 적용 여부는 판 종류가 정한다(GameSim.accountBonus) */
  get bonus(): AccountBonusIn { return this.bonusOut; }

  /** 판 시작(loadMap): 판 처치 수·보스 기록을 비운다 */
  beginRun(): void {
    this.runKills.fill(0);
    this.runBoss = null;
  }
  /** 몹 처치(combat.killMob). 파티 판은 부르지 않는다 */
  noteKill(kind: number): void { if (kind >= 0 && kind < MOB_KIND_COUNT) this.runKills[kind]++; }
  /** 일반 보스를 쓰러뜨렸다(boss.damaged) */
  noteBoss(key: string): void { this.runBoss = key; }
  /** 이번 판에 센 처치 수(시험·진단) */
  runKillCount(kind: number): number { return this.runKills[kind] ?? 0; }
  get runBossKey(): string | null { return this.runBoss; }

  /**
   * 영수증을 반영한다(스테이지·일일 = 판 처치 + 보스, 레이드 = 보스만). 이미 반영한 영수증이면 아무것도 안 한다.
   * region = 1..6 지역 번호(몬스터 칸 `m:<지역>:<종류>`), bossKey = 이 판에서 쓰러뜨린 보스(없으면 null),
   * withKills=false면 판 처치 수를 쓰지 않는다(레이드). 반영하면 점수를 다시 세고 문턱 이벤트를 낸다
   */
  settle(receiptId: string, region: number, bossKey: string | null, withKills = true): boolean {
    const gains: CodexGain[] = [];
    if (withKills && region >= 1 && region <= 6) {
      for (let k = 0; k < MOB_KIND_COUNT; k++) if (this.runKills[k] > 0) gains.push({ id: monsterCellId(region, k), n: this.runKills[k] });
    }
    if (bossKey) gains.push({ id: bossCellId(bossKey), n: 1 });
    const done = this.store.settle(receiptId, gains, this.g.wallClock());
    if (done) this.recompute(true);
    return done;
  }

  /** 저장소에 없는 도감 변경을 쓴다(고정 스텝 밖: GameSim.flushSaves·saveRunProgress·마을 진입) */
  flush(force = false): boolean { return this.store.flush(force); }

  /** 장부·장비·모험·아바타에서 점수를 다시 센다. emit이면 새로 닿은 문턱마다 이벤트를 낸다. 점수가 바뀌었으면 true */
  recompute(emit: boolean): boolean {
    const g = this.g, st = this.store;
    st.read();
    this.countSets();
    const found = g.adventure.found, owned = g.avatar.owned;
    let total = 0;
    for (const c of this.catList) { c.found = 0; c.score = 0; }
    for (let i = 0; i < CELL_COUNT; i++) {
      const cell = this.cellList[i], cat = cell.cat;
      let value = 0;
      if (cat === 'monster' || cat === 'boss') value = st.killsOf(cell.id);
      else if (cat === 'set') value = this.setSlots.get(cell.id)?.size ?? 0;
      else if (cat === 'unique') value = found.includes(cell.id.slice(2)) ? 1 : 0;
      else if (cat === 'avatar') value = owned.includes(cell.id.slice(2)) ? 1 : 0;
      // 펫(L2)은 1단계에서 0
      const stage = cellStage(cat, value), score = cellScore(cat, value);
      this.prevStage[i] = cell.stage;
      cell.value = value; cell.stage = stage; cell.score = score;
      const cv = this.catList[CAT_INDEX[cat]];
      if (stage > 0) cv.found++;
      cv.score += score;
      total += score;
    }
    const before = this.scoreNow;
    this.scoreNow = total;
    // 보너스: 점수 → 도감 열 보너스 → 출처 상한(도감 열)을 한 번 더 건 값
    codexBonus(total, this.rawBonus);
    sumAccountBonus(this.rawBonus, ZERO_ACCOUNT_BONUS, ZERO_ACCOUNT_BONUS, this.bonusOut);
    let steps = 0;
    for (const b of CODEX_BONUS_STEPS) if (total >= b.score) steps++;
    if (total >= CODEX_COMPLETE_STEP.score) steps++;
    if (emit) {
      // 건너뛴 문턱이 여럿이면(한 번에 큰 증가) 새로 닿은 단계마다 이벤트 하나. d = 반영이 끝난 총점
      for (let i = 0; i < CELL_COUNT; i++) {
        const cell = this.cellList[i];
        for (let s = this.prevStage[i] + 1; s <= cell.stage; s++) {
          const e = g.emit(Ev.CodexStep); e.a = i; e.b = s; e.c = CODEX_STEP_SCORES[cell.cat][s - 1]; e.d = total;
        }
      }
      for (let s = this.bonusStepsReached + 1; s <= steps; s++) {
        const e = g.emit(Ev.CodexBonus);
        e.a = s <= CODEX_BONUS_STEPS.length ? CODEX_BONUS_STEPS[s - 1].score : CODEX_COMPLETE_STEP.score; e.b = total; e.c = s;
      }
    }
    this.bonusStepsReached = steps;
    if (before !== total || st.revision !== this.lastStoreRev) this.rev++;
    this.lastStoreRev = st.revision;
    return before !== total;
  }

  /** 세트별 보유 부위(서로 다른 슬롯) 수 집계. 지금 가방·장착에 있는 장비로 센다 */
  private countSets(): void {
    const m = this.setSlots;
    m.clear();
    for (const it of this.g.gear.items) {
      const d = equipDef(it.def);
      if (!d || !d.setId || !SET_INDEX.has(d.setId)) continue;
      const key = 's:' + d.setId;
      let s = m.get(key);
      if (!s) { s = new Set<string>(); m.set(key, s); }
      s.add(d.slot);
    }
  }
}
